import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/permissions";
import { PageHeader, SectionCard, EmptyState } from "@/components/common/Primitives";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { fmtDateTime } from "@/lib/format";
import { Plus, ArrowLeftRight, Truck } from "lucide-react";

const TR_BADGE = {
  unterwegs: { label: "Unterwegs (Transit)", cls: "bg-amber-500/10 text-amber-700 dark:text-amber-300" },
  abgeschlossen: { label: "Abgeschlossen", cls: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300" },
  storniert: { label: "Storniert", cls: "bg-muted text-muted-foreground line-through" },
};

export default function WawiTransfers() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const write = can(user?.role, "wawi:write");
  const [open, setOpen] = useState(false);
  const empty = { from_warehouse: "", to_warehouse: "", article_id: "", variant_id: "", quantity: 1, note: "" };
  const [f, setF] = useState(empty);

  const { data: whs } = useQuery({ queryKey: ["wawi-warehouses"], queryFn: async () => (await api.get("/wawi/warehouses")).data });
  const { data: arts } = useQuery({ queryKey: ["wawi-articles-all"], queryFn: async () => (await api.get("/wawi/articles?limit=200")).data });
  const { data, refetch } = useQuery({ queryKey: ["wawi-transfers"], queryFn: async () => (await api.get("/wawi/transfers")).data });
  const warehouses = whs?.items || [];
  const selArt = (arts?.items || []).find((a) => a.id === f.article_id);

  const create = async () => {
    try {
      await api.post("/wawi/transfers", { ...f, quantity: Number(f.quantity) || 0, variant_id: f.variant_id || "" });
      toast.success("Umlagerung angelegt (unterwegs)"); setOpen(false); setF(empty); refetch();
      qc.invalidateQueries({ queryKey: ["wawi-stock"] });
    } catch (err) { toast.error(apiError(err)); }
  };
  const receive = async (id) => {
    try { await api.post(`/wawi/transfers/${id}/receive`); toast.success("Wareneingang bestätigt"); refetch(); qc.invalidateQueries({ queryKey: ["wawi-stock"] }); }
    catch (err) { toast.error(apiError(err)); }
  };
  const cancel = async (id) => {
    try { await api.post(`/wawi/transfers/${id}/cancel`); toast.success("Umlagerung storniert – Bestand zurückgebucht"); refetch(); qc.invalidateQueries({ queryKey: ["wawi-stock"] }); }
    catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div>
      <PageHeader title="Umlagerungen" description="Mengen zwischen Lagern/Filialen umbuchen – mit Transitbestand"
        actions={write && <Button className="gap-1.5 bg-accent text-accent-foreground hover:bg-accent/90" onClick={() => setOpen(true)} data-testid="new-transfer-button"><Plus className="h-4 w-4" />Neue Umlagerung</Button>} />
      <SectionCard title={`${(data?.items || []).length} Umlagerungen`}>
        {(data?.items || []).length === 0 ? <EmptyState icon={ArrowLeftRight} title="Keine Umlagerungen" /> : (
          <div className="space-y-2">
            {data.items.map((t) => (
              <div key={t.id} className="flex items-center justify-between gap-3 rounded-md border p-3" data-testid={`transfer-row-${t.id}`}>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium">{t.article_name}</span>
                    <Badge variant="outline" className="text-[10px]">{t.quantity} Stk</Badge>
                    <Badge className={TR_BADGE[t.status]?.cls}>{TR_BADGE[t.status]?.label || t.status}</Badge>
                  </div>
                  <div className="text-xs text-muted-foreground">{t.from_name} → {t.to_name} · {fmtDateTime(t.created_at)}</div>
                </div>
                {write && t.status === "unterwegs" && (
                  <div className="flex shrink-0 gap-2">
                    <Button size="sm" variant="outline" className="gap-1.5" onClick={() => receive(t.id)} data-testid={`transfer-receive-${t.id}`}><Truck className="h-4 w-4" /><span className="hidden sm:inline">Eingang bestätigen</span></Button>
                    <Button size="sm" variant="outline" className="text-destructive" onClick={() => cancel(t.id)} data-testid={`transfer-cancel-${t.id}`}>Stornieren</Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </SectionCard>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Neue Umlagerung</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Von Lager</Label>
                <Select value={f.from_warehouse} onValueChange={(v) => setF({ ...f, from_warehouse: v })}>
                  <SelectTrigger data-testid="transfer-from"><SelectValue placeholder="Quelle" /></SelectTrigger>
                  <SelectContent>{warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="space-y-1.5"><Label>Nach Lager</Label>
                <Select value={f.to_warehouse} onValueChange={(v) => setF({ ...f, to_warehouse: v })}>
                  <SelectTrigger data-testid="transfer-to"><SelectValue placeholder="Ziel" /></SelectTrigger>
                  <SelectContent>{warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent>
                </Select></div>
            </div>
            <div className="space-y-1.5"><Label>Artikel</Label>
              <Select value={f.article_id} onValueChange={(v) => setF({ ...f, article_id: v, variant_id: "" })}>
                <SelectTrigger data-testid="transfer-article"><SelectValue placeholder="Artikel wählen" /></SelectTrigger>
                <SelectContent>{(arts?.items || []).map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
              </Select></div>
            {(selArt?.variants || []).length > 0 && (
              <div className="space-y-1.5"><Label>Variante</Label>
                <Select value={f.variant_id || "none"} onValueChange={(v) => setF({ ...f, variant_id: v === "none" ? "" : v })}>
                  <SelectTrigger><SelectValue placeholder="Keine" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">Keine</SelectItem>{selArt.variants.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}</SelectContent>
                </Select></div>
            )}
            <div className="space-y-1.5"><Label>Menge</Label><Input type="number" min="1" value={f.quantity} onChange={(e) => setF({ ...f, quantity: e.target.value })} data-testid="transfer-quantity" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Abbrechen</Button>
            <Button onClick={create} className="bg-accent text-accent-foreground hover:bg-accent/90" data-testid="transfer-save">Umbuchen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
